import React, { useState, useEffect } from "react";
import {
  Pause,
  Play,
  Volume2,
  Loader2,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { QuestionFormData } from "../../types";

type Props = {
  lang: "es" | "ca" | "en" | "ar";
  formData: QuestionFormData;
  setFormData: React.Dispatch<React.SetStateAction<QuestionFormData>>;
  playingAudio: { lang: string | null };
  needsAudioRegeneration: { [key: string]: boolean };
  prevTextEs: React.MutableRefObject<string>;
  prevTextCa: React.MutableRefObject<string>;
  prevTextEn: React.MutableRefObject<string>;
  prevTextAr: React.MutableRefObject<string>;
  regenerateAudio: (lang: "es" | "ca" | "en" | "ar") => void;
  toggleAudio: (lang: "es" | "ca" | "en" | "ar") => void;
  handleFileUpload: (
    lang: "es" | "ca" | "en" | "ar"
  ) => (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  audioRefs: React.MutableRefObject<{
    es?: HTMLAudioElement | null;
    ca?: HTMLAudioElement | null;
    en?: HTMLAudioElement | null;
    ar?: HTMLAudioElement | null;
  }>;
  setPlayingAudio: React.Dispatch<
    React.SetStateAction<{ lang: string | null }>
  >;
  isGeneratingAudio?: boolean;
};

export default function AudioControls({
  lang,
  formData,
  setFormData: _setFormData, // eslint-disable-line @typescript-eslint/no-unused-vars
  playingAudio,
  needsAudioRegeneration,
  prevTextEs: _prevTextEs, // eslint-disable-line @typescript-eslint/no-unused-vars
  prevTextCa: _prevTextCa, // eslint-disable-line @typescript-eslint/no-unused-vars
  prevTextEn: _prevTextEn, // eslint-disable-line @typescript-eslint/no-unused-vars
  prevTextAr: _prevTextAr, // eslint-disable-line @typescript-eslint/no-unused-vars
  regenerateAudio,
  toggleAudio,
  handleFileUpload: _handleFileUpload, // eslint-disable-line @typescript-eslint/no-unused-vars
  audioRefs,
  setPlayingAudio,
  isGeneratingAudio: parentIsGeneratingAudio = false,
}: Props) {
  const { translations } = useAuth();
  const hasText = !!formData[`text_${lang}`];
  const hasAudio = !!(formData.audioUrls?.[lang] && formData.audioUrls[lang].trim());
  const isPlaying = playingAudio.lang === lang;
  
  const [duration, setDuration] = useState(0);
  const showRegenerateButton = needsAudioRegeneration[lang];

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || isNaN(seconds) || seconds === 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Effect para manejar el elemento de audio cuando cambia la URL
  useEffect(() => {
    const audio = audioRefs.current[lang];
    if (!hasAudio || !audio) return;

    const audioUrl = formData.audioUrls?.[lang];
    if (audioUrl && audio.src !== audioUrl) {
      // Si está reproduciendo, detenerlo y resetear antes de cambiar la URL
      if (isPlaying || playingAudio.lang === lang) {
        audio.pause();
        audio.currentTime = 0;
        setPlayingAudio({ lang: null });
      }
      audio.src = audioUrl;
      audio.load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAudio, formData.audioUrls, lang, isPlaying]); // audioRefs y setPlayingAudio son estables

  // Effect para obtener la duración del audio
  useEffect(() => {
    const audio = audioRefs.current[lang];
    if (!audio) return;

    const handleLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleEnded = () => {
      setPlayingAudio({ lang: null });
      if (audio) {
        audio.currentTime = 0;
      }
    };

    // Si ya tiene duración, establecerla
    if (audio.duration && isFinite(audio.duration)) {
      setDuration(audio.duration);
    }

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, hasAudio, formData.audioUrls]); // audioRefs y setPlayingAudio son estables

  const handleRegenerate = async () => {
    await regenerateAudio(lang);
  };

  return (
    <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700">
            {translations[`audio${lang.toUpperCase()}`]}
          </span>
          {showRegenerateButton && hasAudio && (
            <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
              {translations.needsRegeneration}
            </span>
          )}
        </div>

        {/* Regenerate button */}
        {formData.audioEnabled && hasAudio && showRegenerateButton && (
            <button
              type="button"
              onClick={handleRegenerate}
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={parentIsGeneratingAudio}
            >
              {translations.regenerateAudio}
            </button>
          )}
      </div>

      {/* Audio Player */}
      {hasText && hasAudio ? (
        <div className="space-y-2">
          {/* Audio source info */}
          <div className="text-xs text-gray-500 mb-1 flex items-center justify-between">
            <span>{translations.autoGeneratedAudio}</span>
            {parentIsGeneratingAudio && (
              <span className="flex items-center space-x-1 text-blue-600">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span className="text-xs">{translations.generatingAudio}</span>
              </span>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center space-x-3">
            {/* Play/Pause button */}
            <button
              type="button"
              onClick={() => toggleAudio(lang)}
              className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={parentIsGeneratingAudio}
              aria-label={isPlaying ? translations.pause : translations.play}
            >
              {parentIsGeneratingAudio ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5 ml-0.5" />
              )}
            </button>

            {/* Duration */}
            {duration > 0 && !parentIsGeneratingAudio && (
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Volume2 className="w-4 h-4 text-gray-400" />
                <span>{formatTime(duration)}</span>
              </div>
            )}
          </div>
        </div>
      ) : hasText && !hasAudio ? (
        <div className="text-sm text-gray-500 flex items-center space-x-2">
          {parentIsGeneratingAudio ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{translations.generatingAudio}</span>
            </>
          ) : (
            <span>{translations.noAudioAvailable}</span>
          )}
        </div>
      ) : (
        <div className="text-sm text-gray-500">
          {translations.noTextForAudio}
        </div>
      )}

      {/* Hidden audio element */}
      {hasAudio && (
        <audio
          ref={(el) => {
            audioRefs.current[lang] = el;
          }}
          src={formData.audioUrls?.[lang]}
          preload="metadata"
          className="hidden"
        />
      )}
    </div>
  );
}
